import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import BlockIcon from "@material-ui/icons/Block";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import PostponeCustomDialog from "../PostponeCustomDialog";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import formatContactDisplayName from "../../utils/formatContactDisplayName";
import { requestTicketListsRefresh } from "../../utils/ticketRefresh";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	internalBadge: {
		display: "inline-block",
		fontSize: 10,
		fontWeight: 700,
		padding: "2px 7px",
		borderRadius: 10,
		backgroundColor: "rgba(254, 74, 35, 0.12)",
		color: "#fe4a23",
		marginLeft: 6,
		letterSpacing: "0.4px",
		textTransform: "uppercase",
		flexShrink: 0,
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		color: theme.palette.text.primary,
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
		minWidth: 0,
	},

	primaryContent: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flex: 1,
		minWidth: 0,
	},

	primaryMeta: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginLeft: "auto",
		flexShrink: 0,
	},

	contactName: {
		minWidth: 0,
		flex: 1,
		fontWeight: 500,
	},

	ticketNumber: {
		flexShrink: 0,
		fontSize: "0.75rem",
		fontWeight: 600,
		fontVariantNumeric: "tabular-nums",
		color: theme.palette.text.secondary,
	},

	lastMessageTime: {
		flexShrink: 0,
	},

	closedBadge: {
		display: "inline-flex",
		alignItems: "center",
		padding: theme.spacing(0.25, 0.75),
		borderRadius: 10,
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		fontSize: 10,
		fontWeight: 600,
		lineHeight: 1.4,
		flexShrink: 0,
	},

	contactLastMessage: {
		minWidth: 0,
		flex: "1 1 180px",
		paddingRight: 0,
	},

	pendingMessageIcon: {
		fontSize: 16,
		verticalAlign: "middle",
		marginRight: 4,
		color: theme.palette.text.secondary,
	},

	failedMessageIcon: {
		color: theme.palette.error.main,
	},

	secondaryContent: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		marginTop: theme.spacing(0.5),
	},

	secondaryMeta: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		flex: "1 1 220px",
		minWidth: 0,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginLeft: "auto",
	},

	metaTag: {
		display: "inline-flex",
		alignItems: "center",
		maxWidth: "100%",
		padding: "2px 10px",
		borderRadius: 999,
		background: theme.palette.type === "dark" ? "rgba(96, 165, 250, 0.16)" : "#EEF4FF",
		color: theme.palette.type === "dark" ? "#BFDBFE" : "#1E56A0",
		border:
			theme.palette.type === "dark"
				? "1px solid rgba(96, 165, 250, 0.3)"
				: "1px solid #C9DBFA",
		fontSize: "0.75rem",
		fontWeight: 600,
		lineHeight: 1.5,
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		minWidth: 104,
	},

	rejectButton: {
		minWidth: 104,
	},

	pendingActions: {
		display: "flex",
		gap: 8,
		marginLeft: "auto",
		flexShrink: 0,
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			justifyContent: "flex-end",
		},
	},

	attentionActions: {
		display: "flex",
		gap: 8,
		marginLeft: "auto",
		flexShrink: 0,
	},

	postReplyCountdown: {
		minWidth: 48,
		fontVariantNumeric: "tabular-nums",
		fontWeight: 600,
		color: theme.palette.text.secondary,
		textAlign: "center",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	lastSenderAvatar: {
		outline: "5px solid",
		outlineOffset: 0,
		[theme.breakpoints.down("xs")]: {
			outlineWidth: 4,
		},
	},

	lastSenderCustomer: {
		outlineColor:
			theme.palette.type === "dark"
				? "rgba(245, 158, 11, 0.5)"
				: "rgba(217, 119, 6, 0.5)",
	},

	lastSenderOperator: {
		outlineColor:
			theme.palette.type === "dark"
				? "rgba(96, 165, 250, 0.5)"
				: "rgba(37, 99, 235, 0.5)",
	},

	lastSenderUnknown: {
		outlineColor:
			theme.palette.type === "dark"
				? "rgba(107, 114, 128, 0.5)"
				: "rgba(156, 163, 175, 0.5)",
	},

	snoozedContent: {
		alignItems: "stretch",
		flexDirection: "column",
		flexWrap: "nowrap",
		gap: theme.spacing(0.5),
	},

	snoozedMessageRow: {
		display: "flex",
		alignItems: "center",
		minWidth: 0,
		width: "100%",
	},

	snoozedMetaRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: theme.spacing(1),
		minWidth: 0,
	},

	snoozedConnectionTags: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: theme.spacing(1),
		minWidth: 0,
		flexWrap: "wrap",
	},

	avatarIndicatorWrapper: {
		position: "relative",
		display: "inline-flex",
	},

	"@keyframes snoozePulse": {
		"0%, 100%": {
			boxShadow: "0 0 0 0 rgba(245, 158, 11, 0.35)",
		},
		"50%": {
			boxShadow: "0 0 0 5px rgba(245, 158, 11, 0)",
		},
	},

	snoozeClockBadge: {
		position: "absolute",
		right: -5,
		bottom: -5,
		width: 20,
		height: 20,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 2,
		color: theme.palette.type === "dark" ? "#2B2112" : "#5B3A00",
		backgroundColor: theme.palette.type === "dark" ? "#FBBF24" : "#FDE68A",
		border: `2px solid ${theme.palette.background.paper}`,
		animation: "$snoozePulse 2s ease-in-out infinite",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},

	snoozeClockIcon: {
		fontSize: 14,
	},

	snoozeTimePill: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		minWidth: 50,
		padding: "2px 9px",
		borderRadius: 999,
		fontSize: "0.72rem",
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
		lineHeight: 1.5,
		color: theme.palette.type === "dark" ? "#2B2112" : "#78350F",
		backgroundColor: theme.palette.type === "dark" ? "#FBBF24" : "#FEF3C7",
		border:
			theme.palette.type === "dark"
				? "1px solid #D97706"
				: "1px solid #F59E0B",
	},

}));

const QUICK_SNOOZE_OPTIONS = [
	{ seconds: 900, labelKey: "fifteenMinutes" },
	{ seconds: 1800, labelKey: "thirtyMinutes" },
	{ seconds: 3600, labelKey: "oneHour" },
	{ seconds: 7200, labelKey: "twoHours" },
];

const TicketListItem = ({ ticket, ourPhones, showAttentionActions }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [postponing, setPostponing] = useState(false);
	const [postponeAnchorEl, setPostponeAnchorEl] = useState(null);
	const [customDialogOpen, setCustomDialogOpen] = useState(false);
	const [postReplySecondsLeft, setPostReplySecondsLeft] = useState(0);
	const [snoozeSecondsLeft, setSnoozeSecondsLeft] = useState(0);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const contactDisplayName =
		ticket?.internalConversationLabel ||
		formatContactDisplayName(ticket?.contact);
	const isInternalConversation = Boolean(
		ticket?.contact?.number && ourPhones && ourPhones.has(ticket.contact.number)
	);
	const lastSenderKey =
		ticket.lastMessageFromMe === true
			? "operator"
			: ticket.lastMessageFromMe === false
				? "customer"
				: "unknown";
	const lastSenderClass = {
		operator: classes.lastSenderOperator,
		customer: classes.lastSenderCustomer,
		unknown: classes.lastSenderUnknown,
	}[lastSenderKey];

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const deadline = ticket?.postReplySnoozeUntil
			? new Date(ticket.postReplySnoozeUntil).getTime()
			: 0;

		const updateCountdown = () => {
			const secondsLeft = deadline
				? Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
				: 0;
			setPostReplySecondsLeft(secondsLeft);
			if (secondsLeft === 0) {
				setPostponeAnchorEl(null);
				setCustomDialogOpen(false);
			}
		};

		updateCountdown();
		if (!deadline || deadline <= Date.now()) return undefined;

		const interval = setInterval(updateCountdown, 250);
		return () => clearInterval(interval);
	}, [ticket?.postReplySnoozeUntil]);

	useEffect(() => {
		const deadline = ticket?.snoozedUntil
			? new Date(ticket.snoozedUntil).getTime()
			: 0;

		const updateSnoozeCountdown = () => {
			setSnoozeSecondsLeft(
				deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0
			);
		};

		updateSnoozeCountdown();
		if (!deadline || deadline <= Date.now()) return undefined;

		const interval = setInterval(updateSnoozeCountdown, 30000);
		return () => clearInterval(interval);
	}, [ticket?.snoozedUntil]);

	const hasTemporaryPostpone =
		ticket.status === "open" &&
		ticket.attentionStatus === "none" &&
		postReplySecondsLeft > 0;
	const showPostponeActions =
		ticket.status === "open" && (showAttentionActions || hasTemporaryPostpone);
	const formatCountdown = seconds => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
	};
	const formatSnoozeTime = seconds => {
		if (seconds < 3600) return `${Math.max(1, Math.ceil(seconds / 60))} min`;
		if (seconds < 86400) return `${Math.ceil(seconds / 3600)} h`;
		return `${Math.ceil(seconds / 86400)} d`;
	};
	const snoozeTooltip = ticket?.snoozedUntil
		? i18n.t("ticketsList.postpone.activeUntil", {
			date: format(new Date(ticket.snoozedUntil), "dd/MM/yyyy HH:mm"),
			interpolation: { escapeValue: false },
		})
		: "";
	const lastMessagePreview = (ticket.lastMessage || "").replace(
		/^\*[^*\r\n]+:\*\s*(?:\r?\n)?/,
		""
	);
	const renderLastMessagePreview = () => {
		if (!lastMessagePreview) return <br />;

		const failed = ticket._pendingLastMessageStatus === "failed";
		const pending = ticket._pendingLastMessageStatus === "pending";

		return (
			<>
				{(pending || failed) && (
					<Tooltip
						arrow
						placement="top"
						title={i18n.t(
							failed
								? "messagesList.delivery.failed"
								: "messagesList.delivery.pending"
						)}
					>
						{failed ? (
							<BlockIcon
								className={clsx(
									classes.pendingMessageIcon,
									classes.failedMessageIcon
								)}
							/>
						) : (
							<AccessTimeIcon className={classes.pendingMessageIcon} />
						)}
					</Tooltip>
				)}
				<MarkdownWrapper>{lastMessagePreview}</MarkdownWrapper>
			</>
		);
	};
	const renderConnectionTags = () =>
		ticket.internalConnections?.length > 0
			? ticket.internalConnections.map(connection => (
				<div
					key={connection.id}
					className={classes.metaTag}
					title={i18n.t("ticketsList.connectionTitle")}
				>
					{connection.name}
				</div>
			))
			: ticket.whatsappId && (
				<div
					className={classes.metaTag}
					title={i18n.t("ticketsList.connectionTitle")}
				>
					{ticket.whatsapp?.name}
				</div>
			);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
			requestTicketListsRefresh({
				source: "ticket-list-item",
				ticketId: id,
				status: "open",
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleRejectTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "closed",
				userId: null,
				suppressFarewell: true,
				skipReopenPrompt: true,
			});
			requestTicketListsRefresh({
				source: "ticket-list-item",
				ticketId: id,
				status: "closed",
			});
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) {
				setLoading(false);
			}
		}
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	const handleUndoPostpone = async id => {
		try {
			await api.post(`/tickets/${id}/attention/unsnooze`);
			// El backend ya emite la actualizacion del ticket por WebSocket;
			// no se recarga toda la lista para evitar el parpadeo visual.
		} catch (err) {
			toastError(err);
		}
	};

	const showPostponeToast = (label, id) => {
		toast(
			({ closeToast }) => (
				<span>
					{i18n.t("ticketsList.postpone.toastMessage", { label })}
					{"  "}
					<button
						type="button"
						onClick={() => {
							handleUndoPostpone(id);
							closeToast();
						}}
						style={{
							background: "none",
							border: "none",
							color: "inherit",
							textDecoration: "underline",
							cursor: "pointer",
							fontWeight: 600,
							padding: 0,
						}}
					>
						{i18n.t("ticketsList.postpone.undo")}
					</button>
				</span>
			),
			{ autoClose: 6000 }
		);
	};

	const submitPostpone = async (body, label) => {
		setPostponing(true);
		try {
			await api.post(`/tickets/${ticket.id}/attention/snooze`, {
				attentionMessageId: ticket.attentionMessageId,
				...body,
			});
			// El backend ya emite la actualizacion del ticket por WebSocket;
			// no se recarga toda la lista para evitar el parpadeo visual.
			showPostponeToast(label, ticket.id);
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) {
				setPostponing(false);
			}
		}
	};

	const handleOpenPostponeMenu = e => {
		e.stopPropagation();
		setPostponeAnchorEl(e.currentTarget);
	};

	const handleClosePostponeMenu = () => {
		setPostponeAnchorEl(null);
	};

	const handleQuickPostpone = (e, seconds, labelKey) => {
		e.stopPropagation();
		handleClosePostponeMenu();
		submitPostpone(
			{ mode: "duration", durationSeconds: seconds },
			i18n.t(`ticketsList.postpone.${labelKey}`)
		);
	};

	const handlePostponeTomorrow = e => {
		e.stopPropagation();
		handleClosePostponeMenu();
		submitPostpone(
			{ mode: "tomorrow" },
			i18n.t("ticketsList.postpone.tomorrow")
		);
	};

	const handleOpenCustomPostpone = e => {
		e.stopPropagation();
		handleClosePostponeMenu();
		setCustomDialogOpen(true);
	};

	const handleConfirmCustomPostpone = durationSeconds => {
		submitPostpone(
			{ mode: "duration", durationSeconds },
			i18n.t("ticketsList.postpone.more")
		);
	};

	const handleNoResponseNeeded = async e => {
		e.stopPropagation();
		setPostponing(true);
		try {
			await api.post(`/tickets/${ticket.id}/attention/no-response`, {
				attentionMessageId: ticket.attentionMessageId,
			});
			// El backend ya emite la actualizacion del ticket por WebSocket;
			// no se recarga toda la lista para evitar el parpadeo visual.
			toast.success(i18n.t("ticketsList.postpone.noResponseSuccess"));
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) {
				setPostponing(false);
			}
		}
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={() => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<span className={classes.avatarIndicatorWrapper}>
						<Tooltip
							arrow
							placement="right"
							title={i18n.t(`ticketsList.lastSender.${lastSenderKey}`)}
						>
							<Avatar
								src={ticket?.contact?.profilePicUrl}
								className={clsx(classes.lastSenderAvatar, lastSenderClass)}
							/>
						</Tooltip>
						{snoozeSecondsLeft > 0 && (
							<Tooltip arrow placement="right" title={snoozeTooltip}>
								<span className={classes.snoozeClockBadge}>
									<AccessTimeIcon className={classes.snoozeClockIcon} />
								</span>
							</Tooltip>
						)}
					</span>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<div className={classes.primaryContent}>
								<span className={classes.ticketNumber}>#{ticket.id}</span>
								<Typography
									noWrap
									component="span"
									variant="body2"
									color="textPrimary"
									className={classes.contactName}
								>
									{contactDisplayName}
								</Typography>
								{isInternalConversation && (
									<span className={classes.internalBadge}>
										{i18n.t("ticketsList.internalTag")}
									</span>
								)}
							</div>
							<div className={classes.primaryMeta}>
								{ticket.status === "closed" && (
									<span className={classes.closedBadge}>
										{i18n.t("ticketsList.closedTag")}
									</span>
								)}
								{ticket.lastMessage && (
									<Typography
										className={classes.lastMessageTime}
										component="span"
										variant="body2"
										color="textSecondary"
									>
										{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
											<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
										) : (
											<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
										)}
									</Typography>
								)}
							</div>
						</span>
					}
					secondary={
						<div
							className={clsx(classes.secondaryContent, {
								[classes.snoozedContent]: snoozeSecondsLeft > 0,
							})}
						>
							{snoozeSecondsLeft > 0 ? (
								<>
									<div className={classes.snoozedMessageRow}>
										<Typography
											className={classes.contactLastMessage}
											noWrap
											component="span"
											variant="body2"
											color="textSecondary"
										>
											{renderLastMessagePreview()}
										</Typography>
										<Badge
											className={classes.newMessagesCount}
											badgeContent={ticket.unreadMessages}
											classes={{ badge: classes.badgeStyle }}
										/>
									</div>
									<div className={classes.snoozedMetaRow}>
										<div className={classes.snoozedConnectionTags}>
											{renderConnectionTags()}
										</div>
										<Tooltip arrow placement="top" title={snoozeTooltip}>
											<span className={classes.snoozeTimePill}>
												{formatSnoozeTime(snoozeSecondsLeft)}
											</span>
										</Tooltip>
									</div>
								</>
							) : (
							<div className={classes.secondaryMeta}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{renderLastMessagePreview()}
								</Typography>

								{renderConnectionTags()}

								<Badge
									className={classes.newMessagesCount}
									badgeContent={ticket.unreadMessages}
									classes={{
										badge: classes.badgeStyle,
									}}
								/>
							</div>
							)}

							{ticket.status === "pending" && (
								<div className={classes.pendingActions}>
									<ButtonWithSpinner
										color="secondary"
										variant="outlined"
										className={classes.rejectButton}
										size="small"
										loading={loading}
										onClick={() => handleRejectTicket(ticket.id)}
									>
										{i18n.t("ticketsList.buttons.reject")}
									</ButtonWithSpinner>
									<ButtonWithSpinner
										color="primary"
										variant="contained"
										className={classes.acceptButton}
										size="small"
										loading={loading}
										onClick={() => handleAcepptTicket(ticket.id)}
									>
										{i18n.t("ticketsList.buttons.accept")}
									</ButtonWithSpinner>
								</div>
							)}

							{showPostponeActions && (
								<div className={classes.attentionActions}>
									{showAttentionActions ? (
										<ButtonWithSpinner
											color="default"
											variant="outlined"
											size="small"
											loading={postponing}
											onClick={handleNoResponseNeeded}
										>
											{i18n.t("ticketsList.postpone.noResponseNeeded")}
										</ButtonWithSpinner>
									) : (
										<span className={classes.postReplyCountdown}>
											{formatCountdown(postReplySecondsLeft)}
										</span>
									)}
									<ButtonWithSpinner
										color="primary"
										variant="outlined"
										size="small"
										loading={postponing}
										onClick={handleOpenPostponeMenu}
									>
										{i18n.t("ticketsList.postpone.button")}
									</ButtonWithSpinner>
									<Menu
										anchorEl={postponeAnchorEl}
										open={Boolean(postponeAnchorEl)}
										onClose={() => {
											handleClosePostponeMenu();
										}}
									>
										{QUICK_SNOOZE_OPTIONS.map(option => (
											<MenuItem
												key={option.seconds}
												onClick={e => handleQuickPostpone(e, option.seconds, option.labelKey)}
											>
												{i18n.t(`ticketsList.postpone.${option.labelKey}`)}
											</MenuItem>
										))}
										<Divider />
										<MenuItem onClick={handlePostponeTomorrow}>
											{i18n.t("ticketsList.postpone.tomorrow")}
										</MenuItem>
										<MenuItem onClick={handleOpenCustomPostpone}>
											{i18n.t("ticketsList.postpone.more")}
										</MenuItem>
									</Menu>
								</div>
							)}
						</div>
					}
				/>
			</ListItem>
			<Divider variant="inset" component="li" />
			{showPostponeActions && (
				<PostponeCustomDialog
					open={customDialogOpen}
					onClose={() => setCustomDialogOpen(false)}
					onConfirm={handleConfirmCustomPostpone}
				/>
			)}
		</React.Fragment>
	);
};

export default TicketListItem;
